import React, { useState } from "react";
import { ZIcon, ZTooltip } from "@atoms";
import { EIcons } from "@atoms/ZIcon/interface";
import { EmailLogAttachment } from "@/Contracts/Others/SalesActivity";
import { downloadEmailLogAttachment } from "../../../../../services/EmailLogAttachment";
const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return "0 Bytes";
	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const getFileIconByExtension = (extension: string): EIcons => {
	const ext = extension.toLowerCase();
	switch (ext) {
		case "pdf":
			return EIcons.RESOURCEPDF;
		case "doc":
		case "docx":
			return EIcons.RESOURCEDOCUMENT;
		case "xls":
		case "xlsx":
			return EIcons.RESOURCESPREADSHEET;
		case "ppt":
		case "pptx":
			return EIcons.RESOURCEPRESENTATION;
		case "jpg":
		case "jpeg":
		case "png":
		case "gif":
			return EIcons.RESOURCEIMAGE;
		case "zip":
		case "rar":
			return EIcons.FILE;
		default:
			return EIcons.FILE;
	}
};
import "./index.less";

interface ZEmailAttachmentListProps {
	attachments: EmailLogAttachment[];
	emailLogId: string;
}

const ZEmailAttachmentList: React.FC<ZEmailAttachmentListProps> = ({
	attachments,
	emailLogId
}) => {
	const [downloadingIds, setDownloadingIds] = useState<Set<string>>(
		new Set()
	);

	const handleDownload = async (attachment: EmailLogAttachment) => {
		if (downloadingIds.has(attachment.id)) return;

		setDownloadingIds(prev => new Set(prev).add(attachment.id));

		try {
			await downloadEmailLogAttachment(
				emailLogId,
				attachment.id,
				attachment.file_name
			);
		} catch (error) {
			console.error("Failed to download attachment:", error);
		} finally {
			setDownloadingIds(prev => {
				const newSet = new Set(prev);
				newSet.delete(attachment.id);
				return newSet;
			});
		}
	};

	if (!attachments?.length) {
		return null;
	}

	return (
		<div className="z-email-attachment-list">
			<div className="attachment-header">
				<ZIcon name={EIcons.ATTACHMENT} size="sm" className="mr-1" />
				<span className="attachment-count">
					{attachments.length} attachment
					{attachments.length > 1 ? "s" : ""}
				</span>
			</div>
			<div className="attachment-items">
				{attachments.map(attachment => {
					const isDownloading = downloadingIds.has(attachment.id);
					const fileExtension =
						attachment.file_name.split(".").pop()?.toLowerCase() ||
						"";
					const fileIcon = getFileIconByExtension(fileExtension);

					return (
						<div
							key={attachment.id}
							className="attachment-item"
							onClick={() =>
								!isDownloading && handleDownload(attachment)
							}
						>
							<div className="attachment-icon">
								<ZIcon
									name={fileIcon || EIcons.FILE}
									size="sm"
									colorVariant={6}
								/>
							</div>
							<div className="attachment-info">
								<ZTooltip
									title={attachment.file_name}
									placement="top"
								>
									<div className="attachment-name">
										{attachment.file_name}
									</div>
								</ZTooltip>
								<div className="attachment-size">
									{formatFileSize(attachment.size_bytes)}
								</div>
							</div>
							<div className="attachment-download">
								{isDownloading ? (
									<ZIcon name={EIcons.LOADING} size="sm" />
								) : (
									<ZIcon
										name={EIcons.DOWNLOAD}
										size="sm"
										colorVariant={6}
										className="download-icon"
									/>
								)}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default ZEmailAttachmentList;
